// src/components/dashboard/CommitteeReportsSection.jsx
import { useEffect, useState, useRef } from "react";
import { RefreshCw } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import {
  getCommittees,
  getAvailability,
  purchaseReport,
  downloadAllReports,
} from "../../services/committeeService.mjs";
import FetchCommitteeButton from "./FetchCommitteeButton";

export const CommitteeReportsSection = ({ credits, setCredits }) => {
  const { user } = useAuth();
  const [committees, setCommittees] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [availability, setAvailability] = useState({});
  const [loadingCommittee, setLoadingCommittee] = useState(null);

  /* ---------------- FETCH COMMITTEES ON MOUNT ---------------- */
  useEffect(() => {
    const loadCommittees = async () => {
      try {
        setLoading(true);
        const data = await getCommittees();
        // ✅ Handle wrapped or raw arrays
        setCommittees(Array.isArray(data) ? data : data.committees || []);
        setError("");
      } catch (err) {
        console.error(err);
        setError("Failed to load committees.");
      } finally {
        setLoading(false);
      }
    };
    loadCommittees();
  }, []);

  /* ---------------- EXPAND / COLLAPSE HANDLER ---------------- */
  const toggleExpand = async (committeeId) => {
    if (expanded === committeeId) {
      setExpanded(null);
      return;
    }
    setExpanded(committeeId);

    // Always refresh availability data when expanding (don't cache)
    try {
      setLoadingCommittee(committeeId);
      const data = await getAvailability(committeeId);
      console.log("Availability for", committeeId, data);
      setAvailability((prev) => ({ ...prev, [committeeId]: data }));
    } catch (err) {
      console.error("Error loading committee reports:", err);
    } finally {
      setLoadingCommittee(null);
    }
  };

  /* ---------------- HANDLE PURCHASE ---------------- */
  const handlePurchase = async (committeeId, reportId) => {
    try {
      await purchaseReport(committeeId, reportId);
      alert("✅ Purchase successful! Report added to your account.");
      setCredits((prev) => prev - 1);

      // Refresh availability for this committee
      const updated = await getAvailability(committeeId);
      setAvailability((prev) => ({ ...prev, [committeeId]: updated }));
    } catch (err) {
      alert(`❌ ${err.message}`);
    }
  };

  /* ---------------- SMOOTH EXPAND ANIMATION ---------------- */
  const Accordion = ({ isOpen, children }) => {
    const contentRef = useRef(null);
    const [height, setHeight] = useState("0px");

    useEffect(() => {
      if (isOpen) {
        const scrollHeight = contentRef.current.scrollHeight;
        setHeight(`${scrollHeight}px`);
      } else {
        setHeight("0px");
      }
    }, [isOpen, children]);

    return (
      <div
        ref={contentRef}
        style={{
          maxHeight: height,
          overflow: "hidden",
          transition: "max-height 0.35s ease",
        }}
      >
        {children}
      </div>
    );
  };

  /* ---------------- REFRESH HANDLER ---------------- */
  const handleRefresh = async () => {
    try {
      setLoading(true);
      const data = await getCommittees();
      setCommittees(Array.isArray(data) ? data : data.committees || []);
      setError("");
    } catch (err) {
      console.error(err);
      setError("Failed to load committees.");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- RENDER ---------------- */
  if (loading) return <p>Loading committees...</p>;
  if (error) return <p className="error">{error}</p>;

  if (!committees || committees.length === 0) {
    return (
      <section className="dashboard-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', gap: '1rem' }}>
          <h3 style={{ margin: 0, whiteSpace: 'nowrap' }}>My Committees</h3>
          <button
            className="btn-outline small"
            onClick={handleRefresh}
            disabled={loading}
            style={{
              opacity: loading ? 0.6 : 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 'auto',
              padding: '0.4rem 0.75rem'
            }}
          >
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
          </button>
        </div>
        <p className="subtext">
          Committees you've uploaded reports for or have access to.
        </p>
        <div
          style={{
            background: "white",
            borderRadius: "10px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            padding: "3rem 2rem",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "3rem", marginBottom: "1rem", opacity: 0.3 }}>
            📋
          </div>
          <h4 style={{ fontSize: "1.25rem", fontWeight: "600", color: "#111827", marginBottom: "0.5rem" }}>
            No Committees Yet
          </h4>
          <p className="subtext" style={{ maxWidth: "500px", margin: "0 auto", lineHeight: "1.6" }}>
            You haven't uploaded any campaign finance reports yet. Upload your first report above to get started, and committees will appear here automatically.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="dashboard-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', gap: '1rem' }}>
        <h3 style={{ margin: 0, whiteSpace: 'nowrap' }}>My Committees</h3>
        <button
          className="btn-outline small"
          onClick={handleRefresh}
          disabled={loading}
          style={{
            opacity: loading ? 0.6 : 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            minWidth: '36px',
            maxWidth: '36px',
            padding: '0.4rem',
            flexShrink: 0
          }}
        >
          <RefreshCw size={14} className={loading ? 'spin' : ''} />
        </button>
      </div>
      <p className="subtext">
        Committees you've uploaded reports for or have access to.
      </p>

      {committees.map((committee) => {
        const isOpen = expanded === committee.committee_id;
        const data = availability[committee.committee_id];
        const isLoading = loadingCommittee === committee.committee_id;

        return (
          <div
            key={committee.committee_id}
            className="committee-card"
            style={{
              background: "white",
              borderRadius: "10px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
              marginBottom: "1rem",
            }}
          >
            {/* HEADER */}
            <div
              className="committee-header"
              onClick={() => toggleExpand(committee.committee_id)}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "1.25rem 1.5rem",
                cursor: "pointer",
              }}
            >
              <div>
                <h4
                  style={{
                    fontSize: "1.35rem",
                    fontWeight: "700",
                    color: "#111827",
                    marginBottom: "0.35rem",
                  }}
                >
                  {committee.committee_name}
                </h4>
                <p className="subtext" style={{ marginBottom: "0.25rem" }}>
                  Treasurer: {committee.treasurer_name || "—"}
                </p>
                {user?.tier !== "free" && (
                  <a
                    href={`/analytics?committee=${committee.committee_id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    style={{
                      color: "#2563eb",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      textDecoration: "none",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.25rem",
                      marginTop: "0.25rem"
                    }}
                  >
                    View Analytics →
                  </a>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", alignItems: "flex-end" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                  <div className="stat-label">
                    Reports: <strong>{committee.owned_reports_count ?? 0}</strong>
                    {committee.available_reports_count > 0 && (
                      <span style={{ color: "#6b7280" }}> (+{committee.available_reports_count} available)</span>
                    )}
                  </div>
                  <div className="stat-label">
                    Unique Donors: <strong>{committee.unique_named_donors_count ?? 0}</strong>
                  </div>
                  <div className="stat-label">
                    Cash Donations:{" "}
                    <strong>
                      ${(committee.cash_donations ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </strong>
                  </div>
                  <div className="stat-label">
                    In-Kind Donations:{" "}
                    <strong>
                      ${(committee.in_kind_donations ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </strong>
                  </div>
                  <div className="stat-label">
                    Cash Expenses:{" "}
                    <strong>
                      ${(committee.cash_expenses ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </strong>
                  </div>
                  <div className="stat-label">
                    In-Kind Expenses:{" "}
                    <strong>
                      ${(committee.in_kind_expenses ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </strong>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                  {committee.mec_id && (
                    <FetchCommitteeButton
                      mecid={committee.mec_id}
                      userTier={user?.tier}
                      onComplete={handleRefresh}
                    />
                  )}
                  <button
                    className="btn-outline small"
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadAllReports(committee.committee_id, "csv");
                    }}
                  >
                    CSV
                  </button>
                  <button
                    className="btn-outline small"
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadAllReports(committee.committee_id, "xlsx");
                    }}
                  >
                    XLSX
                  </button>
                </div>
              </div>
            </div>

            {/* EXPANDED DETAILS */}
            <Accordion isOpen={isOpen}>
              <div
                className="committee-details"
                style={{
                  padding: "1rem 1.5rem 1.5rem",
                  borderTop: "1px solid #e5e7eb",
                  background: "#f9fafb",
                }}
              >
                {isLoading ? (
                  <p>Loading reports...</p>
                ) : !data ? (
                  <p className="subtext">
                    Click to load committee report details.
                  </p>
                ) : (
                  <>
                    {/* OWNED REPORTS - FIXED FIELD NAMES */}
                    <h5
                      style={{
                        fontSize: "1rem",
                        fontWeight: "600",
                        marginBottom: "0.5rem",
                      }}
                    >
                      Owned Reports ({data.owned_reports?.length || 0})
                    </h5>
                    {data.owned_reports?.length > 0 ? (
                      <ul style={{ marginBottom: "1rem" }}>
                        {data.owned_reports.map((r) => (
                          <li
                            key={r.report_id}
                            style={{
                              padding: "0.35rem 0",
                              borderBottom: "1px solid #e5e7eb",
                              opacity: r.superseded_by ? 0.5 : 1,
                            }}
                          >
                            <div>
                              <span style={{ color: r.superseded_by ? "#9ca3af" : "#111827" }}>
                                {r.filename} —{" "}
                              </span>
                              <span style={{ color: r.superseded_by ? "#9ca3af" : "#6b7280" }}>
                                {r.date_of_report || 'No date'}
                              </span>
                              {r.superseded_by && (
                                <div style={{
                                  marginTop: "0.25rem",
                                  padding: "0.25rem 0.5rem",
                                  backgroundColor: "#f3f4f6",
                                  borderLeft: "3px solid #9ca3af",
                                  fontSize: "0.85rem",
                                  color: "#6b7280",
                                  borderRadius: "4px"
                                }}>
                                  -- Replaced by: {r.superseded_by.filename} ({r.superseded_by.date_of_report})
                                </div>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="subtext">No owned reports yet.</p>
                    )}

                    {/* AVAILABLE REPORTS - FIXED FIELD NAMES */}
                    <h5
                      style={{
                        fontSize: "1rem",
                        fontWeight: "600",
                        marginTop: "1rem",
                        marginBottom: "0.5rem",
                      }}
                    >
                      Available Reports ({data.available_reports?.length || 0})
                    </h5>
                    {data.available_reports?.length > 0 ? (
                      <ul>
                        {data.available_reports.map((r) => (
                          <li
                            key={r.report_id}
                            style={{
                              padding: "0.35rem 0",
                              borderBottom: "1px solid #e5e7eb",
                              opacity: r.superseded_by ? 0.5 : 1,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <span style={{ color: r.superseded_by ? "#9ca3af" : "#111827" }}>
                                  {r.filename} —{" "}
                                </span>
                                <span style={{ color: r.superseded_by ? "#9ca3af" : "#6b7280" }}>
                                  {r.date_of_report || 'No date'}
                                </span>
                                {r.superseded_by && (
                                  <div style={{
                                    marginTop: "0.25rem",
                                    padding: "0.25rem 0.5rem",
                                    backgroundColor: "#f3f4f6",
                                    borderLeft: "3px solid #9ca3af",
                                    fontSize: "0.85rem",
                                    color: "#6b7280",
                                    borderRadius: "4px"
                                  }}>
                                    -- Replaced by: {r.superseded_by.filename} ({r.superseded_by.date_of_report})
                                  </div>
                                )}
                              </div>
                              <button
                                className="btn-primary small"
                                disabled={credits < 1}
                                onClick={() =>
                                  handlePurchase(committee.committee_id, r.report_id)
                                }
                                style={{ marginLeft: "1rem" }}
                              >
                                Purchase (1 credit)
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="subtext">No available reports to purchase.</p>
                    )}
                  </>
                )}
              </div>
            </Accordion>
          </div>
        );
      })}
    </section>
  );
};          